//
/* GS://PROTOCOLO
 * Arquivo: src/pages/AccountsPayablePage.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 2.3 – Integração e Correção de Caminhos
 * Impacto da Versão: [src/pages/AccountsPayablePage.jsx]
 * Data: 25/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [CORREÇÃO]: Ajuste dos imports para `../` (arquivo localizado na raiz de /pages).
 * - [UI]: Adição de tratamento para datas vazias e loading state aprimorado.
 * - [FUNCIONALIDADE]: Integração confirmada com PayAccountModal.
 */
//

import React, { useState, useEffect } from 'react';
import { getAccountsPayable, getSuppliers, getFinancialCategories } from '../services/firestoreService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddAccountPayableForm from '../components/forms/AddAccountPayableForm';
import PayAccountModal from '../components/modals/PayAccountModal';
import { 
    PlusCircle, 
    CheckCircle, 
    Calendar,
    AlertCircle
} from 'lucide-react';

const AccountsPayablePage = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedAccountToPay, setSelectedAccountToPay] = useState(null);
    
    const [suppliersMap, setSuppliersMap] = useState(new Map());
    const [categoriesMap, setCategoriesMap] = useState(new Map());

    const loadData = async () => {
        setLoading(true);
        try {
            const [accData, suppData, catData] = await Promise.all([
                getAccountsPayable(),
                getSuppliers(),
                getFinancialCategories()
            ]);

            setAccounts(accData);

            const sMap = new Map();
            suppData.forEach(s => sMap.set(s.id, s.name));
            setSuppliersMap(sMap);

            const cMap = new Map();
            catData.forEach(c => cMap.set(c.id, c.name));
            setCategoriesMap(cMap);

        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            toast.error("Falha ao carregar contas.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Ação de Pagar
    const handlePayClick = (account) => {
        setSelectedAccountToPay(account);
    };

    const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    
    const formatDate = (date) => {
        if (!date) return '-';
        // Se for string ISO
        if (typeof date === 'string') return new Date(date).toLocaleDateString('pt-BR');
        // Se for Timestamp do Firestore
        if (date.toDate) return date.toDate().toLocaleDateString('pt-BR');
        // Se for objeto Date
        return date.toLocaleDateString('pt-BR');
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            paid: 'bg-green-100 text-green-800 border-green-200',
            overdue: 'bg-red-100 text-red-800 border-red-200',
            partially_paid: 'bg-blue-100 text-blue-800 border-blue-200'
        };
        const labels = {
            pending: 'Pendente',
            paid: 'Pago',
            overdue: 'Vencida',
            partially_paid: 'Parcial'
        };
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Cabeçalho */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Calendar className="text-brand-gold" /> Contas a Pagar
                    </h1>
                    <p className="text-sm text-gray-500">Gerencie seus compromissos financeiros e fornecedores</p>
                </div>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center bg-brand-gold text-brand-black hover:bg-yellow-500 font-bold py-2 px-4 rounded-lg shadow transition"
                >
                    <PlusCircle className="mr-2" size={20} />
                    Nova Conta
                </button>
            </div>

            {/* Modal de Adição */}
            {showAddForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl overflow-hidden">
                        <AddAccountPayableForm
                            onClose={() => setShowAddForm(false)}
                            onAccountAdded={loadData}
                        />
                    </div>
                </div>
            )}

            {/* Modal de Pagamento */}
            {selectedAccountToPay && (
                <PayAccountModal 
                    account={selectedAccountToPay}
                    onClose={() => setSelectedAccountToPay(null)}
                    onSuccess={() => {
                        loadData();
                        setSelectedAccountToPay(null);
                    }}
                />
            )}

            {/* Tabela */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
                {loading ? (
                    <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold mb-2"></div>
                        Carregando contas...
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="p-12 text-center flex flex-col items-center text-gray-400">
                        <AlertCircle size={48} className="mb-4 opacity-20" />
                        <p className="text-lg font-medium">Nenhuma conta a pagar encontrada.</p>
                        <p className="text-sm mt-1">Clique em "Nova Conta" para cadastrar.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-gray-100 text-gray-600 uppercase font-bold text-xs border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3">Vencimento</th>
                                    <th className="px-6 py-3">Descrição</th>
                                    <th className="px-6 py-3">Fornecedor</th>
                                    <th className="px-6 py-3">Categoria</th>
                                    <th className="px-6 py-3 text-right">Valor</th>
                                    <th className="px-6 py-3 text-center">Status</th>
                                    <th className="px-6 py-3 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {accounts.map((acc) => (
                                    <tr key={acc.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-700 whitespace-nowrap">
                                            {formatDate(acc.dueDate)}
                                        </td>
                                        <td className="px-6 py-4 font-semibold text-gray-900">
                                            {acc.description}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {suppliersMap.get(acc.supplierRef?.id || acc.supplierRef) || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                {categoriesMap.get(acc.categoryRef?.id) || 'Geral'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-gray-800 whitespace-nowrap">
                                            {formatCurrency(acc.amount)}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {getStatusBadge(acc.status)}
                                        </td>
                                        <td className="px-6 py-4 flex justify-center gap-2">
                                            {acc.status === 'pending' || acc.status === 'overdue' ? (
                                                <button 
                                                    onClick={() => handlePayClick(acc)}
                                                    className="flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded hover:bg-green-100 transition-colors font-medium text-xs shadow-sm"
                                                    title="Realizar Pagamento"
                                                >
                                                    <CheckCircle size={14} /> Pagar
                                                </button>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic flex items-center gap-1">
                                                    <CheckCircle size={12} /> Concluído
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountsPayablePage;